import { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../providers/authprovider";
import RajcompanyLogo from "../../components/RajcompanyLogo";

const MAX_SECTIONS = 8;

// Logo i pieczątka pochodzą ze spółek (rajcompanies) - trzymamy tylko id spółki.
const emptySection = () => ({
  logo_rajcompany_id: null,
  title: "",
  address: "",
  postal_city: "",
  phone: "",
  email: "",
});

// Treść startowa, gdy w bazie nie ma jeszcze żadnego szablonu.
const DEFAULT_CONFIG = {
  stamp_rajcompany_id: null,
  sections: [
    {
      ...emptySection(),
      title: "ZACHÓD/POŁUDNIE",
      address: "ul. Produkcyjna 4",
      postal_city: "55-330 Błonie",
      phone: "+48 71 7156275",
      email: "biuro@controlsystem.pl",
    },
    {
      ...emptySection(),
      title: "CENTRUM",
      address: "ul. E.Habicha 5 m.115",
      postal_city: "02-495 Warszawa",
      phone: "605-533-755",
      email: "sekretariat@controlsystem.pl",
    },
    {
      ...emptySection(),
      title: "PÓŁNOC",
      address: "ul. E.Habicha 5 m.115",
      postal_city: "02-495 Warszawa",
      phone: "603-133-988",
      email: "sekretariat@controlsystem.pl",
    },
    {
      ...emptySection(),
      title: "WSCHÓD",
      address: "ul. Dembowskiego 5 p.101",
      postal_city: "32-020 Wieliczka",
      phone: "+48 12 3768410",
      email: "sekretariat@controlsystem.pl",
    },
  ],
};

const asList = (content) => (Array.isArray(content) ? content : []);

const toConfig = (page) => ({
  stamp_rajcompany_id: page.stamp_rajcompany_id ?? null,
  sections: asList(page.sections)
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((section) => ({ ...emptySection(), ...section })),
});

const findCompany = (companies, id) =>
  companies?.find((company) => String(company.id) === String(id));

function CompanySelect({ companies, value, onChange, flag, emptyLabel }) {
  return (
    <Form.Select
      size="sm"
      value={value ?? ""}
      onChange={(e) => onChange(findCompany(companies, e.target.value)?.id ?? null)}
    >
      <option value="">{emptyLabel}</option>
      {(companies ?? [])
        .filter((company) => company[flag])
        .map((company) => (
          <option key={company.id} value={company.id}>
            {company.full_name}
          </option>
        ))}
    </Form.Select>
  );
}

function SectionForm({ index, count, section, companies, onPatch, onMove, onRemove }) {
  const handleChange = (e) => onPatch({ [e.target.name]: e.target.value });

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center py-1">
        <span>Sekcja {index + 1}</span>
        <div>
          <Button
            size="sm"
            variant="link"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            title="Przesuń w górę"
          >
            <FontAwesomeIcon icon={faArrowUp} />
          </Button>
          <Button
            size="sm"
            variant="link"
            disabled={index === count - 1}
            onClick={() => onMove(1)}
            title="Przesuń w dół"
          >
            <FontAwesomeIcon icon={faArrowDown} />
          </Button>
          <Button
            size="sm"
            variant="link"
            className="text-danger"
            disabled={count === 1}
            onClick={onRemove}
            title="Usuń sekcję"
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="py-2">
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Logo</Form.Label>
          <CompanySelect
            companies={companies}
            value={section.logo_rajcompany_id}
            flag="has_logo"
            emptyLabel="Brak logo"
            onChange={(id) => onPatch({ logo_rajcompany_id: id })}
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Tytuł</Form.Label>
          <Form.Control size="sm" name="title" value={section.title ?? ""} onChange={handleChange} />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Adres</Form.Label>
          <Form.Control
            size="sm"
            name="address"
            value={section.address ?? ""}
            onChange={handleChange}
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Kod pocztowy i miejscowość</Form.Label>
          <Form.Control
            size="sm"
            name="postal_city"
            value={section.postal_city ?? ""}
            onChange={handleChange}
          />
        </Form.Group>
        <Row>
          <Col>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-0">Telefon</Form.Label>
              <Form.Control
                size="sm"
                name="phone"
                value={section.phone ?? ""}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-0">E-mail</Form.Label>
              <Form.Control
                size="sm"
                type="email"
                name="email"
                value={section.email ?? ""}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

function LastPagePreview({ config, companies }) {
  const has = (id, flag) => !!findCompany(companies, id)?.[flag];

  return (
    <div
      className="border bg-white p-4 d-flex flex-column small"
      style={{ aspectRatio: "210 / 297", fontSize: "0.75rem" }}
    >
      {config.sections.map((section, index) => (
        <div key={index} className="mb-3">
          {section.logo_rajcompany_id && (
            <div className="mb-1">
              <RajcompanyLogo
                companyId={section.logo_rajcompany_id}
                hasLogo={has(section.logo_rajcompany_id, "has_logo")}
                height={36}
              />
            </div>
          )}
          {section.title && <div className="fw-bold">{section.title}</div>}
          {section.address && <div>{section.address}</div>}
          {section.postal_city && <div>{section.postal_city}</div>}
          {section.phone && <div>tel. {section.phone}</div>}
          {section.email && <div>{section.email}</div>}
        </div>
      ))}
      <div className="mt-auto">
        {config.stamp_rajcompany_id && (
          <RajcompanyLogo
            companyId={config.stamp_rajcompany_id}
            hasLogo={has(config.stamp_rajcompany_id, "has_stamp")}
            type="stamp"
            height={70}
          />
        )}
      </div>
    </div>
  );
}

export default function LastPageModal({ show, onHide }) {
  const { rajcompanies, lastPages } = useAuth();
  // `page` - zapisana wersja (id szablonu, version_id, ...); null gdy w bazie nie ma
  // jeszcze szablonu i pierwszy zapis go utworzy.
  const [page, setPage] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [companies, setCompanies] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Bez udanego wczytania nie wiadomo, czy szablon istnieje - zapis mógłby
  // utworzyć duplikat zamiast nowej wersji.
  const [loadFailed, setLoadFailed] = useState(false);

  const loadPage = () => {
    setLoading(true);
    setError("");
    setLoadFailed(false);

    const fail = (error) => {
      setLoading(false);
      setLoadFailed(true);
      setError(error);
    };

    lastPages.getAll(({ success, content, error }) => {
      if (!success) return fail(error);

      const templates = asList(content);
      const template = templates.find((p) => p.is_default) ?? templates[0];

      if (!template) {
        setPage(null);
        setConfig(DEFAULT_CONFIG);
        return setLoading(false);
      }

      lastPages.get(template.id, ({ success, content, error }) => {
        if (!success) return fail(error);

        setLoading(false);
        setPage(content);
        setConfig(toConfig(content));
      });
    });
  };

  // Kontekst `useAuth()` nie jest memoizowany - efekt zależy tylko od `show`.
  useEffect(() => {
    if (!show) return;

    loadPage();
    rajcompanies.get(null, ({ success, content }) => setCompanies(success ? asList(content) : []));
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSections = (sections) => setConfig((old) => ({ ...old, sections }));

  const handleCountChange = (e) => {
    const count = Number(e.target.value);
    const sections = config.sections.slice(0, count);
    while (sections.length < count) sections.push(emptySection());
    setSections(sections);
  };

  const handleSectionPatch = (index, patch) =>
    setConfig((old) => ({
      ...old,
      sections: old.sections.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }));

  const handleMove = (index, direction) => {
    const sections = [...config.sections];
    const [moved] = sections.splice(index, 1);
    sections.splice(index + direction, 0, moved);
    setSections(sections);
  };

  const handleRemove = (index) => {
    setSections(config.sections.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const data = {
      stamp_rajcompany_id: config.stamp_rajcompany_id,
      sections: config.sections.map((section, position) => ({
        position,
        logo_rajcompany_id: section.logo_rajcompany_id,
        title: section.title,
        address: section.address,
        postal_city: section.postal_city,
        phone: section.phone,
        email: section.email,
      })),
    };

    const onSaved = ({ success, content, error }) => {
      setSaving(false);
      if (!success) return setError(error);

      setPage(content);
      setConfig(toConfig(content));
      onHide();
    };

    setSaving(true);
    setError("");

    if (page) lastPages.save(page.id, data, onSaved);
    else lastPages.create({ ...data, name: "Domyślna", is_default: true }, onSaved);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable backdrop={saving ? "static" : true}>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          Ostatnia strona protokołu
          {page?.version && (
            <small className="text-muted fs-6 ms-3">
              wersja {page.version}
              {page.created_at && ` · ${new Date(page.created_at).toLocaleString("pl-PL")}`}
              {page.created_by_name && ` · ${page.created_by_name}`}
            </small>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger">
            {error}
            {loadFailed && (
              <div className="mt-2">
                <Button size="sm" variant="outline-danger" onClick={loadPage}>
                  Spróbuj ponownie
                </Button>
              </div>
            )}
          </Alert>
        )}

        {loading ? (
          <div className="text-muted">
            <Spinner size="sm" className="me-2" />
            Pobieranie ostatniej strony...
          </div>
        ) : (
          <Row>
            <Col lg={7}>
              <Form.Group className="mb-3" style={{ maxWidth: 200 }}>
                <Form.Label>Liczba sekcji</Form.Label>
                <Form.Select value={config.sections.length} onChange={handleCountChange}>
                  {Array.from({ length: MAX_SECTIONS }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {config.sections.map((section, index) => (
                <SectionForm
                  key={index}
                  index={index}
                  count={config.sections.length}
                  section={section}
                  companies={companies}
                  onPatch={(patch) => handleSectionPatch(index, patch)}
                  onMove={(direction) => handleMove(index, direction)}
                  onRemove={() => handleRemove(index)}
                />
              ))}

              <Card className="mb-3">
                <Card.Header className="py-1">Pieczątka (dół strony)</Card.Header>
                <Card.Body className="py-2">
                  <CompanySelect
                    companies={companies}
                    value={config.stamp_rajcompany_id}
                    flag="has_stamp"
                    emptyLabel="Brak pieczątki"
                    onChange={(id) => setConfig((old) => ({ ...old, stamp_rajcompany_id: id }))}
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col lg={5}>
              <div className="position-sticky" style={{ top: 0 }}>
                <div className="mb-2 text-muted small">Podgląd</div>
                <LastPagePreview config={config} companies={companies} />
              </div>
            </Col>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          className="me-auto"
          disabled={loading || saving}
          onClick={() => setConfig(DEFAULT_CONFIG)}
        >
          Przywróć domyślne
        </Button>
        <Button variant="secondary" disabled={saving} onClick={onHide}>
          Anuluj
        </Button>
        <Button onClick={handleSave} disabled={loading || saving || loadFailed}>
          {saving ? <Spinner size="sm" /> : "Zapisz"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
